import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { LinkProps } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button, ButtonProps } from 'src/components/Button';
import { Container, ContainerProps } from 'src/components/Container';
import { DrawerLayout } from 'src/components/Drawer';
import { Icon, IconDefinition } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { PageHeader } from 'src/components/PageHeader';
import { SafeArea } from 'src/components/SafeArea';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import { useTheme } from 'src/components/muiTheme/utils';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { Grow } from 'src/components/transitions/Grow';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { isTrainer } from 'src/models/user/utils';
import { useReloadChannel } from 'src/shared/useReloadChannel';

import { WebviewBackButton } from '../WebviewBackButton';

import { DownloadAppBanner } from './DownloadAppBanner';
import { HelloBar } from './HelloBar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { MainNav } from './MainNav';
import { TopBar } from './TopBar';

export interface ButtonPropsWithLabel extends ButtonProps {
  label: string;
  icon?: IconDefinition;
}

const MainNavWidth = '280px';

export interface AppFrameProps {
  children: React.ReactNode;
  drawerOpen: boolean;
  title: React.ReactNode;
  collapsedTitle?: React.ReactNode;
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  row?: React.ReactNode;

  /**
   * frameOnly: Skips Container wrapper and PageHeader but keeps all navigation chrome
   * - Navigation sidebar remains visible
   * - Top bar and banners remain visible
   * - Mobile header remains visible
   * - Only removes the page container and header wrapper around content
   * - Use for: Custom page layouts that need navigation but want full control over content layout
   */
  frameOnly?: boolean;

  compactTitle?: boolean;

  /**
   * noChrome: Removes main navigation chrome but keeps some page structure
   * - Removes navigation sidebar, top bar, and banners
   * - Keeps mobile header on small screens
   * - Keeps page header on desktop (when frameOnly is false)
   * - Keeps container wrapping
   * - Set via URL parameter: ?nochrome=true
   * - Use for: Minimal views, webview embedding
   */
  noChrome?: boolean;

  containerMaxWidth?: ContainerProps['maxWidth'];
  backButtonTitle?: string;
  backButtonLinkTo?: LinkProps['to'] | (() => void);
  isAdmin?: boolean;
  bgcolor: string;

  /**
   * disabled: Completely disables the app frame - renders only children
   * - Removes ALL app frame elements (navigation, headers, containers, banners)
   * - Renders only the raw page content
   * - No navigation, no headers, no containers, no chrome of any kind
   * - Use for: True iframe embedding, completely isolated page rendering
   */
  disabled?: boolean;
}

export const AppFrame: React.FC<AppFrameProps> = ({
  drawerOpen,
  title,
  collapsedTitle,
  actions,
  row,
  frameOnly,
  compactTitle,
  noChrome,
  backButtonTitle,
  backButtonLinkTo,
  bgcolor,
  containerMaxWidth = 'lg',
  isAdmin = false,
  disabled = false,
  children,
}) => {
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const xlDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));
  const { user, sessionTrainer, session } = useOptionalSession();

  // Subscribe to reload channel for admin-triggered page reloads
  useReloadChannel();

  const resetThemeColors = {
    color: 'text.primary',
    bgcolor: bgcolor || 'background.default',
    transition: 'background-color 0.5s',
  };

  // Wait for user and sessionTrainer to load before evaluating banner conditions
  const showDownloadBanner =
    !!user && // Check if user exists
    !!sessionTrainer && // Check if sessionTrainer exists
    !isAdmin &&
    !isTrainer(user) &&
    !user.hideMobileAppBanner &&
    !session?.hideAppCta;

  const [navToggled, setNavToggled] = useState(!xlDown);
  const { isWebview, isInIframe } = useNativeBridge();
  const navOpen = navToggled;

  // If disabled or in iframe, render only the children without any app frame elements
  if (disabled || isInIframe) {
    return <>{children}</>;
  }

  if (noChrome) {
    return (
      <Box sx={resetThemeColors}>
        {smDown && (
          <MobileAppHeader
            title={title}
            collapsedTitle={collapsedTitle}
            actions={actions}
            row={row}
            compactTitle={true}
            bgcolor={bgcolor}
          />
        )}
        <Box sx={{ width: '100%' }}>
          <DrawerLayout drawerOpen={drawerOpen}>
            {!!frameOnly && <>{children}</>}

            {!frameOnly && (
              <Container maxWidth={containerMaxWidth} sx={{ mx: 'auto' }}>
                {!smDown && (
                  <PageHeader
                    backButtonLinkTo={backButtonLinkTo}
                    title={title}
                    actions={actions}
                    row={row}
                    backButtonTitle={backButtonTitle}
                  />
                )}
                {children}
              </Container>
            )}
          </DrawerLayout>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={resetThemeColors}>
      {smDown && (
        <>
          {!isWebview && (
            <>
              <ImpersonationBanner />
              {showDownloadBanner ? <DownloadAppBanner /> : <HelloBar />}
            </>
          )}
          <MobileAppHeader
            title={title}
            collapsedTitle={collapsedTitle}
            actions={actions}
            row={row}
            onClickHamburger={() => setNavToggled(!navOpen)}
            compactTitle={compactTitle}
            bgcolor={bgcolor}
          />
        </>
      )}
      <Box
        sx={{
          paddingLeft: navOpen && !xlDown ? MainNavWidth : 0,
          width: '100%',
          display: 'flex',
          transition: (theme) =>
            theme.transitions.create('padding', {
              easing: drawerOpen
                ? theme.transitions.easing.easeOut
                : theme.transitions.easing.sharp,
              duration: drawerOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <MainNav
          open={navOpen}
          toggleOpen={() => xlDown && setNavToggled(!navOpen)}
          navWidth={MainNavWidth}
          isAdmin={isAdmin}
        />
        <Box sx={{ width: '100%' }}>
          {!smDown && (
            <>
              <TopBar
                toggleOpenNav={() => setNavToggled(!navOpen)}
                isAdmin={isAdmin}
                bgcolor={bgcolor}
              ></TopBar>
              {!isWebview && (
                <>
                  <ImpersonationBanner />
                  {showDownloadBanner ? <DownloadAppBanner /> : <HelloBar />}
                </>
              )}
            </>
          )}
          <DrawerLayout drawerOpen={drawerOpen}>
            {!!frameOnly && <>{children}</>}

            {!frameOnly && (
              <Container
                maxWidth={containerMaxWidth}
                sx={{ mx: 'auto', zIndex: 1 }}
              >
                {!smDown && (
                  <PageHeader
                    backButtonLinkTo={backButtonLinkTo}
                    title={title}
                    actions={actions}
                    row={row}
                    backButtonTitle={backButtonTitle}
                  />
                )}
                {children}
              </Container>
            )}
          </DrawerLayout>
        </Box>
      </Box>
    </Box>
  );
};

export interface MobileAppHeaderProps {
  title: React.ReactNode;
  collapsedTitle?: React.ReactNode;
  onClickBack?: () => void;
  onClickHamburger?: () => void;
  navigation?: React.ReactNode;
  row?: React.ReactNode;
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  compactTitle?: boolean;
  bgcolor?: string;
}

export const MobileAppHeader: React.FC<MobileAppHeaderProps> = ({
  title,
  collapsedTitle,
  actions,
  row,
  onClickHamburger,
  navigation,
  compactTitle,
  bgcolor,
}) => {
  const theme = useTheme();
  const { isWebview, inset } = useNativeBridge();

  const { ref: bigTitleRef, inView: bigTitleInView } = useInView({
    threshold: 1,
  });

  return (
    <>
      <SafeArea
        top
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '55px',
          position: 'sticky',
          top: '0px',
          bgcolor: bgcolor || 'background.default',
          zIndex: theme.zIndex?.appBar,
          transition: 'background-color 0.5s',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ minWidth: '56px' }}
          p={1}
        >
          {!isWebview && onClickHamburger && (
            <IconButton onClick={onClickHamburger} sx={{ mr: 1 }}>
              <Icon
                size="medium"
                color="action"
                icon={faBars}
              />
            </IconButton>
          )}
          {isWebview && <WebviewBackButton />}
          {navigation && navigation}
          <Grow in={!bigTitleInView || compactTitle === true}>
            <Typography
              noWrap
              variant="h6"
              sx={{ ml: 1, textTransform: 'capitalize' }}
            >
              {collapsedTitle || title}
            </Typography>
          </Grow>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          p={1}
          sx={{
            flexShrink: 0,
            minWidth: '56px',
            flexDirection: 'row-reverse',
          }}
        >
          {Array.isArray(actions) &&
            actions.map((buttonProps, i) => (
              <>
                {buttonProps.icon ? (
                  <IconButton key={i} {...buttonProps}>
                    <Icon
                      color="action"
                      size="medium"
                      icon={buttonProps.icon}
                    />
                  </IconButton>
                ) : (
                  <Button key={i} {...buttonProps}>
                    {buttonProps.label}
                  </Button>
                )}
              </>
            ))}
          {!Array.isArray(actions) && actions}
        </Stack>
      </SafeArea>
      {compactTitle !== true && (
        <Typography
          noWrap
          variant="h4"
          sx={{ mt: 2, mb: 3, px: 2, textTransform: 'capitalize' }}
          ref={bigTitleRef}
        >
          {title}
        </Typography>
      )}
      {row && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          pl={2}
          pb={1}
          sx={{
            position: 'sticky',
            top: `calc(env(safe-area-inset-top, ${inset.top + 'px'}) + 56px)`,
            bgcolor: 'background.default',
            zIndex: theme.zIndex?.appBar,
          }}
        >
          {row}
        </Stack>
      )}
    </>
  );
};
